import type { HealthCheckStatus } from '@gonfalon/health-check/types';
import { msg } from '@gonfalon/messages/msg';

import { isLDHosted } from '../../isLDHosted';
import { MetricKind } from '../../MetricKind';

import type { HealthCheckItem, MetricDefinition, MetricHealthResponse } from './constants';

export const isWithinLast90Days = (ts: number): boolean => {
  const now = Date.now();
  const ninetyDays = 90 * 24 * 60 * 60 * 1000;
  return ts >= now - ninetyDays;
};

const getEventKind = (metric: MetricDefinition) => {
  if (metric.kind === 'click') {
    return MetricKind.CLICK;
  } else if (metric.kind === 'pageview') {
    return MetricKind.PAGEVIEW;
  } else if (metric.kind === 'custom') {
    if (metric.isNumeric) {
      return MetricKind.NUMERIC;
    } else if (metric.unitAggregationType === 'sum') {
      return MetricKind.COUNT;
    } else if (metric.unitAggregationType === 'average') {
      return MetricKind.BINARY;
    }
  }
};

export const getHealthList = (
  eventKey: string,
  metric: MetricDefinition,
  isError: boolean,
  data: MetricHealthResponse,
): HealthCheckItem[] => {
  const eventKind = getEventKind(metric);
  const showExplicitWarning = metric.successCriteria === 'LowerThanBaseline' && eventKind === MetricKind.BINARY;
  // Case A: missing eventKey
  if (!eventKey) {
    return [
      {
        status: 'waiting',
        message: msg('metrics.healthCheck.noEventKey'),
      },
    ];
  }

  // Case B: missing randomization units
  if (!metric.analysisUnits || metric.analysisUnits?.length === 0) {
    return [
      {
        status: 'waiting',
        message: msg('metrics.healthCheck.noRandomizationUnits'),
      },
    ];
  }

  // Case C: API error or missing data
  if (isError || !data) {
    return [
      {
        status: 'error',
        message: msg('metrics.healthCheck.loadError'),
      },
    ];
  }

  // Case D: the real health‐check components
  // Calculate statuses
  const eventsSeen = data.lastSeen && isWithinLast90Days(data.lastSeen);

  if (!eventsSeen) {
    return [
      {
        status: 'warning',
        message: msg('metrics.healthCheck.noEvents90Days'),
      },
    ];
  }

  const evaluationPeriodDays = metric.dataSource && isLDHosted(metric.dataSource?.key) ? '14' : '7';
  const randomizationUnitsDetails: { status: HealthCheckStatus; message: string } = (() => {
    const overlappingRandomizationUnits = data.contextKinds ?? [];
    const randomizationUnits = metric.analysisUnits ?? [];
    const someMatchingUnits = randomizationUnits.some((randomizationUnit) =>
      overlappingRandomizationUnits.includes(randomizationUnit),
    );
    const allMatchingUnits = randomizationUnits.every((randomizationUnit) =>
      overlappingRandomizationUnits.includes(randomizationUnit),
    );
    if (overlappingRandomizationUnits.length === 0) {
      return {
        status: 'warning',
        message: msg('metrics.healthCheck.noUnitEvents', { days: evaluationPeriodDays }),
      };
    }
    if (!someMatchingUnits) {
      return {
        status: showExplicitWarning ? 'warning' : 'fail',
        message: msg('metrics.healthCheck.unitsNoEvents', { days: evaluationPeriodDays }),
      };
    }
    if (allMatchingUnits) {
      return {
        status: 'pass',
        message: msg('metrics.healthCheck.allUnitsHaveEvents', { days: evaluationPeriodDays }),
      };
    }
    return {
      status: 'warning',
      message: msg('metrics.healthCheck.someUnitsHaveEvents', { days: evaluationPeriodDays }),
    };
  })();

  const numericValuesDetails: { status: HealthCheckStatus; message: string } = (() => {
    if (!metric.isNumeric || !isLDHosted(metric.dataSource?.key)) {
      return {
        status: 'waiting',
        message: '',
      };
    }
    if (!data.sampleInstancesSeen) {
      return {
        status: 'warning',
        message: msg('metrics.healthCheck.noNumericValues'),
      };
    }
    if (data.numericValuesFound && data.allValuesNumeric) {
      return {
        status: 'pass',
        message: msg('metrics.healthCheck.allNumericValues'),
      };
    }
    if (data.numericValuesFound) {
      return {
        status: 'warning',
        message: msg('metrics.healthCheck.someNumericValues'),
      };
    }
    return {
      status: showExplicitWarning ? 'warning' : 'fail',
      message: msg('metrics.healthCheck.noSampleNumericValues'),
    };
  })();

  return [
    {
      status: 'pass',
      message: msg('metrics.healthCheck.eventsReceived90Days'),
    },
    {
      status: randomizationUnitsDetails.status,
      message: randomizationUnitsDetails.message,
    },
    {
      status: numericValuesDetails.status,
      message: numericValuesDetails.message,
    },
  ];
};
